import {
  UtensilsCrossed, MapPin, Calendar, User, Users 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import type { Menu } from '../../types';
import {
  MENU_TYPE_LABELS, MENU_STATUS_LABELS, MENU_STATUS_COLORS 
} from '../../types';

interface MenuCardProps {readonly menu: Menu;}

const ALLERGEN_ICONS: Record<string, string> = {
  fish: '🐟',
  shellfish: '🦐',
  dairy: '🥛',
  eggs: '🥚',
  wheat: '🌾',
  peanuts: '🥜',
  'tree nuts': '🌰',
  soy: '🫘',
  sesame: '🌱',
};

function getUtilizationColor(utilization: number): string {
  if (utilization > 90) return 'text-red-600';
  if (utilization > 70) return 'text-amber-600';
  return 'text-green-600';
}

export function MenuCard({ menu }: MenuCardProps) {
  const statusColor = MENU_STATUS_COLORS[menu.status];
  const utilization = menu.venueCapacity && menu.defaultPax
    ? Math.round((menu.defaultPax / menu.venueCapacity) * 100)
    : null;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <UtensilsCrossed className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base font-medium">{menu.name}</CardTitle>
          </div>
          <span className={`rounded-full px-2 py-1 text-xs font-medium ${statusColor}`}>
            {MENU_STATUS_LABELS[menu.status]}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">{MENU_TYPE_LABELS[menu.type]}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <MapPin className="h-4 w-4" />
          <span>{menu.venueName}</span>
        </div>

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4" />
          <span>From: {new Date(menu.effectiveFrom).toLocaleDateString()}</span>
        </div>

        {/* Pax and Capacity */}
        {(menu.defaultPax ?? menu.venueCapacity) && (
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Users className="h-4 w-4" />
              <span>{menu.defaultPax?.toLocaleString() ?? '-'} pax</span>
            </div>
            {menu.venueCapacity && (
              <span className="text-muted-foreground">/ {menu.venueCapacity.toLocaleString()} capacity</span>
            )}
            {utilization !== null && (
              <span className={`text-xs font-medium ${getUtilizationColor(utilization)}`}>
                ({utilization}%)
              </span>
            )}
          </div>
        )}

        {menu.description && (
          <p className="text-sm text-muted-foreground italic">{menu.description}</p>
        )}

        <div className="pt-2 border-t">
          <p className="text-xs text-muted-foreground mb-2">Menu Items ({menu.totalItems}):</p>
          <div className="space-y-2">
            {menu.items.slice(0, 4).map((item) => (
              <div key={item.menuItemId} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 flex-1 truncate">
                  <span className={item.isAvailable ? '' : 'text-muted-foreground line-through'}>{item.recipeName}</span>
                  {item.allergens.length > 0 && (
                    <span className="text-xs">{item.allergens.slice(0, 3).map((a) => ALLERGEN_ICONS[a] ?? '⚠️').join('')}</span>
                  )}
                </div>
                {item.price !== undefined && item.price > 0 && (
                  <span className="text-muted-foreground ml-2">${item.price}</span>
                )}
              </div>
            ))}
            {menu.items.length > 4 && (
              <p className="text-xs text-muted-foreground">+{menu.items.length - 4} more items</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2 border-t">
          <User className="h-3 w-3" />
          <span>Created by {menu.createdByName}</span>
        </div>
      </CardContent>
    </Card>
  );
}
